import { CheckCircle2, DollarSign, Truck, Headset, FileCheck, Laptop, Users, LineChart, Megaphone } from "lucide-react";
import { Reveal, PageHero } from "@/components/bits";
import { CarrierForm, AgentForm } from "@/components/forms";

const WRAP = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

function Benefits({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative z-10 -mt-24 lg:-mt-28">
      {items.map((b, i) => (
        <Reveal key={b.title} delay={0.06 * i} className="h-full">
          <div className="card card-hover h-full p-7" data-testid={`benefit-card-${i}`}>
            <div className="h-11 w-11 rounded-2xl bg-signal/10 flex items-center justify-center">
              <b.icon className="h-5 w-5 text-corpblue" />
            </div>
            <h3 className="mt-5 font-display text-lg font-bold text-ink">{b.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

function Checklist({ items, testid }) {
  return (
    <div className="panel-reference p-8 lg:p-12" data-testid={testid}>
      <p className="label text-brand-light">Requirements</p>
      <h2 className="mt-3 font-display text-2xl font-bold text-white">What you'll need on file</h2>
      <ul className="mt-7 space-y-4">
        {items.map((r) => (
          <li key={r} className="flex items-start gap-3 text-slate-200">
            <CheckCircle2 className="h-5 w-5 text-gold shrink-0 mt-0.5" />
            <span className="text-[15px] leading-relaxed">{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CarriersPage() {
  const benefits = [
    { icon: Truck, title: "Consistent loads", body: "Steady freight across California and the Southwest, with lanes that repeat weekly." },
    { icon: DollarSign, title: "Quick pay options", body: "Fast-payment options on approved loads — no waiting on invoices to age out." },
    { icon: FileCheck, title: "Fair terms, in writing", body: "Fuel, detention and lumper terms quoted up front and honored at settlement." },
    { icon: Headset, title: "A dispatcher who answers", body: "Live dispatch support day and night for every truck under our load." },
  ];
  return (
    <main>
      <PageHero
        overlap
          eyebrow="For Carriers"
          title="Get quality freight at your fingertips."
          lead="Carriers are the backbone of our industry. We build relationships on trust, transparency and fast, fair pay — so your truck stays loaded and paid."
      />
      <section className="pb-16 lg:pb-24">
        <div className={WRAP}>
          <Benefits items={benefits} />
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <CarrierForm />
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.12}>
                <Checklist
                  testid="carrier-requirements"
                  items={[
                    "Active MC authority (operating authority, not pending)",
                    "Current insurance certificate — auto liability at or above $750,000 and cargo coverage",
                    "W-9 signed by the company owner on file",
                    "Signed broker-carrier agreement — direct only, we never double-broker",
                    "Safety rating satisfactory or not rated",
                  ]}
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export function AgentsPage() {
  const benefits = [
    { icon: Laptop, title: "Best tools in the industry", body: "Modern TMS, load boards, market-rate data and a CRM that keeps your book organized." },
    { icon: Users, title: "Back office, handled", body: "Licensing, bonding, billing, collections and carrier vetting run by our team." },
    { icon: LineChart, title: "Splits that reward growth", body: "Commission splits tailored to your book — talk to us about your lanes and volume." },
    { icon: Megaphone, title: "Marketing support", body: "Brand, website and collateral backing so you pitch shippers with confidence." },
  ];
  return (
    <main>
      <PageHero
        overlap
          eyebrow="Agent Program"
          title="Run your book. We run the back office."
          lead="Our agents are an extension of our own team — collaboration focused on mutual growth, with unparalleled support every step of the way."
      />
      <section className="pb-16 lg:pb-24">
        <div className={WRAP}>
          <Benefits items={benefits} />
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <AgentForm />
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.12}>
                <div className="panel-reference p-8 lg:p-12" data-testid="agent-program-panel">
                  <p className="label text-brand-light">How it works</p>
                  <h2 className="mt-3 font-display text-2xl font-bold text-white">Three steps to your own desk</h2>
                  <ol className="mt-7 space-y-6">
                    {[
                      ["Apply below", "Send your details — a short call follows within one business day."],
                      ["Design your package", "Splits, territories and tools shaped to your book of business."],
                      ["Start moving freight", "Operate under our license and authority with full back-office support."],
                    ].map(([t, b], i) => (
                      <li key={t} className="flex gap-4">
                        <span className="h-9 w-9 shrink-0 rounded-full bg-white text-corpblue font-display font-bold flex items-center justify-center">{i + 1}</span>
                        <div>
                          <p className="font-display font-bold text-white">{t}</p>
                          <p className="mt-1 text-slate-300 text-sm leading-relaxed">{b}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
